import { NetworkStatus, useQuery } from "@apollo/client";
import DailyEventsCalendar from "components/investorrelations/DailyEventsCalendar";
import lightFormat from "date-fns/lightFormat";
import AgendaQuery from "lib/queries/AgendaQuery";
import type { IAgendaQuery, IAgendaQueryVariables } from "lib/queries/__generated__/AgendaQuery.generated";
import parseDateYmd from "lib/utils/parseDateYmd";
import debounce from "lodash/fp/debounce";
import { Value } from "react-powerplug";
import { IOrderDirection } from "types/graphqlTypes";
import { ArrowDown10Icon, ArrowUp01Icon } from "lucide-react";
import RightSideFilters from "./RightSideFilters";
import type { IEventsCalendarProps } from "./types";
import { EventsCalendarFilterRange } from "./types";
import { getAgendaEventType, getAgendaIssuerType } from "./utils";

export default function EventsCalendar({
  filterState,
  setFilterState,
  filterByEventTypes,
  isRenderInsidePortfolio,
}: IEventsCalendarProps) {
  // We don't want to allow the user to query for the whole year without a search query
  const isYearlyFilteringWithoutSearch =
    filterState.selectedFilter === EventsCalendarFilterRange.InTheYear && !filterState.searchQuery;

  const { data, previousData, loading, error, fetchMore, networkStatus } = useQuery<
    IAgendaQuery,
    IAgendaQueryVariables
  >(AgendaQuery, {
    skip: isYearlyFilteringWithoutSearch,
    notifyOnNetworkStatusChange: true,
    variables: {
      filterByStartDate: lightFormat(filterState.startDate, "yyyy-MM-dd"),
      filterByEndDate: lightFormat(filterState.endDate, "yyyy-MM-dd"),
      filterByEventType: filterState.eventType ? getAgendaEventType(filterState.eventType) : filterByEventTypes ?? null,
      filterByIssuerType: filterState.issuerType ? getAgendaIssuerType(filterState.issuerType) || null : null,
      filterByIssuersInPortfolio: filterState.myPositions ?? null,
      filterByIssuersInBookmarks: filterState.isInBookmarks ?? null,
      filterBySearchQuery: filterState.searchQuery ?? null,
      filterByIsInSnP500: filterState.isInSP500 ?? null,
      filterByIssuerIds: null,
      direction: filterState.orderBy || IOrderDirection.Descending,
      limit: 30,
      cursor: null,
    },
  });

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;

  function loadMoreEvents() {
    if (!data) {
      return;
    }

    const { agenda } = data;
    fetchMore({
      variables: {
        cursor: agenda.pageInfo.cursor,
      },
      updateQuery: (previousQueryResult, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.agenda) {
          return previousQueryResult;
        }

        return {
          ...previousQueryResult,
          agenda: {
            ...fetchMoreResult.agenda,
            nodes: previousQueryResult.agenda.nodes?.concat(fetchMoreResult.agenda.nodes),
          },
        };
      },
    });
  }

  return (
    <>
      <div className="col-span-12 row-start-2 space-y-4 lg:col-span-8 lg:row-start-1">
        <div className="flex items-center space-x-4">
          <Value
            initial={filterState.searchQuery}
            onChange={debounce(400, (searchQuery) => {
              const safeSearchQuery = searchQuery.trim();

              setFilterState({
                ...filterState,
                searchQuery: safeSearchQuery,
              });
            })}
          >
            {({ value, set }) => (
              <input
                type="text"
                className="flex w-full rounded-md border-gray-300 shadow-sm placeholder:tracking-wide placeholder:text-gray-400 focus:border-indigo-500 focus:ring-indigo-500"
                value={value}
                onChange={(event) => set(event.target.value)}
                placeholder="Pesquise ativos pelo código de negociação..."
              />
            )}
          </Value>

          <button
            className="h-6 w-6 text-gray-400 hover:cursor-pointer hover:text-indigo-500"
            type="button"
            onClick={() => {
              setFilterState({
                ...filterState,
                orderBy:
                  filterState?.orderBy === IOrderDirection.Ascending
                    ? IOrderDirection.Descending
                    : IOrderDirection.Ascending,
              });
            }}
          >
            {filterState.orderBy === IOrderDirection.Ascending ? (
              <ArrowUp01Icon strokeWidth={1.5} className="h-6 w-6" />
            ) : (
              <ArrowDown10Icon strokeWidth={1.5} className="h-6 w-6" />
            )}
          </button>
        </div>

        {!isYearlyFilteringWithoutSearch && (
          <DailyEventsCalendar
            events={data}
            isLoading={loading}
            isLoadingMore={isLoadingMore}
            onLoadMoreEvents={() => loadMoreEvents()}
            onGoToNextAgendaEvent={() => {
              if (data && data.agenda.nextUpcomingEventDateRange) {
                setFilterState({
                  ...filterState,
                  startDate: parseDateYmd(data.agenda.nextUpcomingEventDateRange.filterByStartDate),
                  endDate: parseDateYmd(data.agenda.nextUpcomingEventDateRange.filterByEndDate),
                });
              }
            }}
          />
        )}

        {isYearlyFilteringWithoutSearch && (
          <div className="rounded-md bg-yellow-50 p-4 font-aeonik font-normal tracking-wide">
            <div className="flex">
              <div className="flex-1 md:flex md:justify-between">
                <p className="text-sm text-amber-700">
                  {`A utilização do filtro "Todo Período" requer o uso da busca por ativo!`}
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 p-4 font-aeonik font-normal tracking-wide">
            <div className="flex">
              <div className="flex-1 md:flex md:justify-between">
                <p className="text-sm text-rose-700">
                  Não foi possível carregar os eventos. Por favor, tente novamente mais tarde.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="col-span-12 space-y-4 lg:col-span-4">
        <RightSideFilters
          onChange={({ startDate, endDate }) => {
            setFilterState({
              ...filterState,
              selectedFilter: EventsCalendarFilterRange.InSelectedPeriod,
              startDate,
              endDate: endDate ?? filterState?.endDate,
            });
          }}
          filterState={filterState}
          setFilterState={setFilterState}
          breakdown={(data ?? previousData)?.agenda.breakdown}
          startDate={filterState.startDate ?? new Date()}
          endDate={filterState.endDate ?? new Date()}
          isRenderInsidePortfolio={isRenderInsidePortfolio}
          totalEvents={data?.agenda.totalCount ?? 0}
        />
      </div>
    </>
  );
}
